import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RotateCcw, UserX } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { UserAPI, extractError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const ROLES = ["owner", "service_advisor", "technician", "parts_manager", "accountant"];

export default function UsersPage() {
    const { t } = useT();
    const { branches, permissions, user: me } = useAuth();
    const canCreate = permissions.includes("user:create");
    const canUpdate = permissions.includes("user:update");
    const canDeactivate = permissions.includes("user:deactivate");
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [density, setDensity] = useState("comfortable");
    const [search, setSearch] = useState("");

    async function load() {
        setLoading(true);
        try {
            setList(await UserAPI.list());
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (u) =>
                u.email.toLowerCase().includes(q) ||
                (u.name || "").toLowerCase().includes(q) ||
                u.role.includes(q)
        );
    }, [list, search]);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="users-page">
            <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        {t("nav.users")}
                    </h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        Invite teammates, assign roles, scope branches. Soft-deactivate to revoke access.
                    </p>
                </div>
                {canCreate && <UserDialog mode="create" branches={branches} onSaved={load} />}
            </header>

            <Card className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200 p-3 dark:border-zinc-800">
                    <Input
                        placeholder={t("common.search")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-xs"
                        data-testid="users-search"
                    />
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            {t("common.density")}
                        </span>
                        <Button
                            variant={density === "compact" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setDensity("compact")}
                            data-testid="density-compact"
                            className={density === "compact" ? "bg-brand-red text-white" : ""}
                        >
                            {t("common.compact")}
                        </Button>
                        <Button
                            variant={density === "comfortable" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setDensity("comfortable")}
                            data-testid="density-comfortable"
                            className={density === "comfortable" ? "bg-brand-red text-white" : ""}
                        >
                            {t("common.comfortable")}
                        </Button>
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">User</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">
                                {t("users.role")}
                            </TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">
                                {t("users.branch")}
                            </TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-wider">Status</TableHead>
                            <TableHead className="text-right font-mono text-[10px] uppercase tracking-wider">
                                Actions
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} className="py-6 text-center text-sm text-zinc-500">
                                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && filtered.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="py-6 text-center text-sm text-zinc-500">
                                    {t("users.empty")}
                                </TableCell>
                            </TableRow>
                        )}
                        {filtered.map((u) => (
                            <TableRow
                                key={u.id}
                                data-testid={`user-row-${u.email}`}
                                className={cn(density === "compact" ? "[&>td]:py-2" : "[&>td]:py-4")}
                            >
                                <TableCell>
                                    <div className="font-medium">{u.name}</div>
                                    <div className="font-mono text-[11px] text-zinc-500">{u.email}</div>
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant="outline"
                                        className="border-brand-redSoft bg-brand-redSoft font-mono text-[10px] uppercase text-brand-redDeep dark:border-brand-red/40 dark:bg-brand-red/10 dark:text-brand-red"
                                    >
                                        {u.role}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {u.branch_ids.length === 0 && <span className="text-xs text-zinc-400">—</span>}
                                        {u.branch_ids.map((bid) => {
                                            const b = branches.find((x) => x.id === bid);
                                            return (
                                                <span
                                                    key={bid}
                                                    className="rounded border border-zinc-200 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:border-zinc-700 dark:text-zinc-400"
                                                >
                                                    {b?.code || bid.slice(-4)}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {u.is_active ? (
                                        <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-400">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                            active
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900">
                                            inactive
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        {canUpdate && (
                                            <UserDialog mode="edit" branches={branches} user={u} onSaved={load} />
                                        )}
                                        {u.is_active ? (
                                            canDeactivate && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="text-rose-600"
                                                    disabled={u.id === me?.id}
                                                    onClick={async () => {
                                                        try {
                                                            await UserAPI.deactivate(u.id);
                                                            toast.success("User deactivated");
                                                            load();
                                                        } catch (e) {
                                                            toast.error(extractError(e));
                                                        }
                                                    }}
                                                    data-testid={`user-deactivate-${u.email}`}
                                                >
                                                    <UserX className="mr-1 h-4 w-4" />
                                                    {t("users.deactivate")}
                                                </Button>
                                            )
                                        ) : (
                                            canUpdate && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={async () => {
                                                        try {
                                                            await UserAPI.activate(u.id);
                                                            toast.success("User activated");
                                                            load();
                                                        } catch (e) {
                                                            toast.error(extractError(e));
                                                        }
                                                    }}
                                                    data-testid={`user-activate-${u.email}`}
                                                >
                                                    <RotateCcw className="mr-1 h-4 w-4" />
                                                    {t("users.activate")}
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
}

function UserDialog({ mode, user, branches, onSaved }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const initial = () =>
        user
            ? {
                  email: user.email,
                  name: user.name,
                  role: user.role,
                  branch_ids: user.branch_ids,
                  default_branch_id: user.default_branch_id,
                  password: "",
              }
            : {
                  email: "",
                  name: "",
                  role: "service_advisor",
                  branch_ids: branches.length > 0 ? [branches[0].id] : [],
                  default_branch_id: branches[0]?.id,
                  password: "",
              };
    const [form, setForm] = useState(initial);
    useEffect(() => {
        if (open) setForm(initial());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }
    function toggleBranch(id) {
        setForm((f) => {
            const has = f.branch_ids.includes(id);
            const next = has ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id];
            const newDefault = next.includes(f.default_branch_id) ? f.default_branch_id : next[0] || null;
            return { ...f, branch_ids: next, default_branch_id: newDefault };
        });
    }

    async function save() {
        setBusy(true);
        try {
            if (mode === "create") {
                await UserAPI.create(form);
                toast.success("User invited");
            } else {
                const body = {
                    name: form.name,
                    role: form.role,
                    branch_ids: form.branch_ids,
                    default_branch_id: form.default_branch_id,
                };
                await UserAPI.update(user.id, body);
                toast.success("User updated");
            }
            setOpen(false);
            onSaved && onSaved();
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {mode === "create" ? (
                    <Button
                        className="bg-brand-red text-white hover:bg-brand-redDeep"
                        data-testid="user-create-open"
                    >
                        <Plus className="mr-1 h-4 w-4" /> Invite user
                    </Button>
                ) : (
                    <Button size="sm" variant="outline" data-testid={`user-edit-${user.email}`}>
                        Edit
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "Invite user" : `Edit ${user.email}`}</DialogTitle>
                    <DialogDescription>
                        Assign a role and one or more branches. Password policy: 10+ chars, letters + digits.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="Email" required>
                        <Input
                            type="email"
                            value={form.email}
                            disabled={mode === "edit"}
                            onChange={(e) => set("email", e.target.value)}
                            data-testid="user-email"
                        />
                    </Field>
                    <Field label="Name" required>
                        <Input
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            data-testid="user-name"
                        />
                    </Field>
                    <Field label="Role" required>
                        <select
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                            value={form.role}
                            onChange={(e) => set("role", e.target.value)}
                            data-testid="user-role"
                        >
                            {ROLES.map((r) => (
                                <option key={r} value={r}>
                                    {r}
                                </option>
                            ))}
                        </select>
                    </Field>
                    {mode === "create" && (
                        <Field label="Initial password" required>
                            <Input
                                type="password"
                                value={form.password}
                                onChange={(e) => set("password", e.target.value)}
                                placeholder="Min 10 chars · letters + digits"
                                data-testid="user-password"
                            />
                        </Field>
                    )}
                    <div className="md:col-span-2">
                        <Field label="Branch access">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="h-9 w-full justify-start font-normal"
                                        data-testid="user-branches"
                                    >
                                        {form.branch_ids.length === 0
                                            ? "Select branches…"
                                            : form.branch_ids
                                                  .map((id) => branches.find((b) => b.id === id)?.code)
                                                  .filter(Boolean)
                                                  .join(", ")}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-72">
                                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-zinc-500">
                                        Branches
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {branches.map((b) => (
                                        <DropdownMenuCheckboxItem
                                            key={b.id}
                                            checked={form.branch_ids.includes(b.id)}
                                            onCheckedChange={() => toggleBranch(b.id)}
                                            data-testid={`user-branch-${b.code}`}
                                        >
                                            <span className="font-mono text-xs uppercase text-brand-redDeep dark:text-brand-red mr-2">
                                                {b.code}
                                            </span>
                                            {b.name}
                                        </DropdownMenuCheckboxItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </Field>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={busy}
                        data-testid="user-save"
                        className="bg-brand-red text-white hover:bg-brand-redDeep"
                    >
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}
                {required && <span className="ml-0.5 text-brand-red">*</span>}
            </Label>
            {children}
        </div>
    );
}
